import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Users, Phone, Video, Paperclip, X, Search } from "lucide-react";
import { toast } from "sonner";
import { MessageBubble } from "../components/MessageBubble";
import { PhotoViewer } from "../components/PhotoMessage";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { useOrders } from "../context/OrdersContext";
import { useCall } from "../context/CallContext";
import { Avatar } from "../components/Avatar";
import { OrderCard } from "../components/OrderCard";
import { ru } from "../locales/ru";

dayjs.locale("ru");

const dayLabel = (iso) => dayjs(iso).format("D MMMM");

export default function ChatView() {
  const { chatId: routeChatId, userId } = useParams();
  const { user } = useAuth();
  const { chats, online, send, subscribe, setActiveChat, markReadLocal } = useChat();
  const { orders, ensureOrders, missingOrders, failedOrders } = useOrders();
  const { startCall } = useCall();
  const navigate = useNavigate();

  const [chatId, setChatId] = useState(routeChatId || null);
  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState("");
  const [typingName, setTypingName] = useState(null);
  const [reply, setReply] = useState(null);
  const [editing, setEditing] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [lastRead, setLastRead] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const jumpingRef = useRef(false);
  const quoteTargetRef = useRef(null);
  const lastRenderedIdRef = useRef(null);
  const closeViewer = useCallback(() => setViewer(null), []);
  useEffect(() => {
    if (!photo) { setPhotoUrl(null); return; }
    const url = URL.createObjectURL(photo); setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  useEffect(() => {
    const read = chats.find(c => c.id === chatId)?.counterpart_last_read_message_id;
    if (read) setLastRead(prev => !prev || read > prev ? read : prev);
  }, [chats, chatId]);

  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);
  const prependingRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const peerRef = useRef(peer);
  peerRef.current = peer;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const isGlobal = chatId === "global";

  const markRead = useCallback((cid, msgs) => {
    const lastReal = [...msgs].reverse().find((m) => !m.temp);
    if (cid && lastReal) {
      send("mark_read", { chat_id: cid, message_id: lastReal.id });
      markReadLocal(cid);
    }
  }, [send, markReadLocal]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (userId) {
          const { data } = await api.get(`/chats/with/${userId}`);
          if (cancelled) return;
          setPeer({
            id: data.user.id,
            name: data.user.name,
            avatar: data.user.avatar,
            online: data.chat?.peer?.online,
            mutual: data.user.mutual ?? data.chat?.peer?.mutual ?? false,
          });
          if (data.chat) setChatId(data.chat.id);
          else setChatId(null);
        } else if (routeChatId && routeChatId !== "global") {
          const c = chats.find((x) => x.id === routeChatId);
          if (c?.peer) setPeer(c.peer);
          else {
            const { data } = await api.get("/chats");
            const found = data.find((x) => x.id === routeChatId);
            if (found?.peer) setPeer(found.peer);
          }
        }
      } catch {}
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, routeChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveChat(chatId);
    if (!chatId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    api.get(`/chats/${chatId}/messages`, { params: { limit: 50 } }).then(({ data }) => {
      if (cancelled) return;
      const msgs = [...data.messages].reverse();
      setMessages(msgs);
      setHasMore(data.has_more);
      if (data.counterpart_last_read_message_id) setLastRead(data.counterpart_last_read_message_id);
      markRead(chatId, msgs);
    }).catch(() => {});
    return () => {
      cancelled = true;
      setActiveChat(null);
    };
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const unsub = subscribe((ev) => {
      if (ev.event === "new_message") {
        const { message, temp_id, chat } = ev.data;
        const mine = message.sender_id === user?.id;
        const belongsHere =
          message.chat_id === chatIdRef.current ||
          (!chatIdRef.current && mine && temp_id && messagesRef.current.some((m) => m.id === temp_id)) ||
          (!chatIdRef.current && chat?.peer?.id === peerRef.current?.id && chat?.type === "direct");
        if (!belongsHere) return;
        if (!chatIdRef.current) setChatId(message.chat_id);
        setMessages((prev) => {
          if (temp_id && prev.some((m) => m.id === temp_id)) {
            return prev.map((m) => (m.id === temp_id ? { ...message } : m));
          }
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
        if (!mine) {
          setTypingName(null);
          if (atBottomRef.current) markRead(message.chat_id, [message]);
        }
      } else if (["message_edited", "message_deleted"].includes(ev.event) && ev.data.message.chat_id === chatIdRef.current) {
        const updated = ev.data.message;
        const current = messagesRef.current.find(m => m.id === updated.id);
        if (current && ((current.revision || 1) > (updated.revision || 1) || (current.deleted && !updated.deleted))) return;
        setMessages(prev => prev.map(m => m.id === updated.id ? updated : m.reply_to === updated.id ? { ...m, reply: { id: updated.id, sender_name: updated.sender_name, text: updated.deleted ? ru.chats.deleted : updated.type === "image" ? ru.chats.photo : updated.text?.slice(0, 160) } } : m));
        setViewer(v => v?.id === updated.id && updated.deleted ? null : v);
        setReply(r => r?.id === updated.id ? updated : r);
        setEditing(e => e?.id === updated.id && updated.deleted ? null : e);
      } else if (ev.event === "read_receipt" && ev.data.chat_id === chatIdRef.current && ev.data.user_id !== user?.id) {
        setLastRead(prev => !prev || ev.data.message_id > prev ? ev.data.message_id : prev);
      } else if (ev.event === "typing" && ev.data.chat_id === chatIdRef.current && ev.data.user_id !== user?.id) {
        setTypingName(ev.data.name);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingName(null), 3000);
      } else if (ev.event === "__reconnected" && chatIdRef.current) {
        const cid = chatIdRef.current;
        const oldest = messagesRef.current.find(m => !m.temp)?.id;
        (async () => {
          let before, all = [], more;
          do {
            const { data } = await api.get(`/chats/${cid}/messages`, { params: { before, limit: 50 } });
            if (chatIdRef.current !== cid) return;
            all.push(...data.messages); more = data.has_more;
            before = data.messages.at(-1)?.id;
            if (data.counterpart_last_read_message_id) setLastRead(data.counterpart_last_read_message_id);
          } while (more && oldest && before > oldest);
          setMessages(prev => [...all.reverse(), ...prev.filter(m => m.temp)]);
          setHasMore(more);
        })().catch(() => {});
      }
    });
    return unsub;
  }, [subscribe, user?.id, markRead]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(entries => {
      ensureOrders(entries.filter(e => e.isIntersecting).map(e => e.target.dataset.orderId));
    }, { root });
    root.querySelectorAll("[data-order-id]").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [messages, ensureOrders]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    const newLast = last?.id !== lastRenderedIdRef.current;
    lastRenderedIdRef.current = last?.id;
    if (quoteTargetRef.current) {
      const target = document.getElementById(`message-${quoteTargetRef.current}`);
      if (target) { target.scrollIntoView({ block: "center" }); target.animate([{ outline: "3px solid #34C759" }, { outline: "3px solid transparent" }], { duration: 1800 }); quoteTargetRef.current = null; }
      return;
    }
    if (jumpingRef.current) return;
    if (prependingRef.current != null) {
      el.scrollTop = el.scrollHeight - prependingRef.current;
      prependingRef.current = null;
      return;
    }
    if (atBottomRef.current || (newLast && last && last.sender_id === user?.id)) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, user?.id]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || jumpingRef.current) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (atBottomRef.current) markRead(chatId, messages);
    if (el.scrollTop < 50 && hasMore && messages.length && chatId) {
      const oldest = messages.find((m) => !m.temp);
      if (!oldest) return;
      setHasMore(false);
      prependingRef.current = el.scrollHeight - el.scrollTop;
      api.get(`/chats/${chatId}/messages`, { params: { before: oldest.id, limit: 50 } }).then(({ data }) => {
        setMessages((prev) => [...[...data.messages].reverse(), ...prev]);
        setHasMore(data.has_more);
      }).catch(() => setHasMore(true));
    }
  };

  const doSend = (body, tempId, replyTo) => {
    const payload = { text: body, temp_id: tempId, reply_to: replyTo || null };
    if (chatIdRef.current) payload.chat_id = chatIdRef.current;
    else payload.peer_id = peerRef.current?.id;
    const ok = send("send_message", payload);
    setTimeout(() => {
      setMessages((prev) => prev.map((m) => (m.id === tempId && m.temp ? { ...m, status: "failed" } : m)));
    }, 10000);
    if (!ok) {
      setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, status: "failed" } : m)));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if ((!body && !photo) || (!chatId && !peer) || uploading) return;
    if (editing) {
      try {
        await api.patch(`/chats/${chatId}/messages/${editing.id}`, { text: body });
        setEditing(null); setText("");
      } catch (err) { toast.error(err.response?.data?.detail || ru.errors.generic); }
      return;
    }
    if (photo) {
      setUploading(true); setUploadFailed(false); setProgress(0);
      try {
        let cid = chatIdRef.current;
        if (!cid) { const { data } = await api.post(`/chats/with/${peer.id}`); cid = data.id; setChatId(cid); chatIdRef.current = cid; }
        const chunkSize = 512 * 1024, count = Math.ceil(photo.size / chunkSize), uploadId = crypto.randomUUID();
        let sent;
        for (let i = 0; i < count; i++) {
          const form = new FormData();
          form.append("file", photo.slice(i * chunkSize, (i + 1) * chunkSize), photo.name);
          form.append("text", body); if (reply) form.append("reply_to", reply.id);
          form.append("upload_id", uploadId); form.append("chunk_index", i); form.append("chunk_count", count);
          const { data } = await api.post(`/chats/${cid}/photos`, form, { onUploadProgress: e => setProgress(Math.round((i + e.loaded / (e.total || 1)) / count * 100)) });
          sent = data;
        }
        setMessages(prev => prev.some(m => m.id === sent.id) ? prev : [...prev, sent]);
        setPhoto(null); setText(""); setReply(null); atBottomRef.current = true;
      } catch (err) { setUploadFailed(true); toast.error(err.response?.data?.detail || ru.chats.photoFailed); }
      finally { setUploading(false); }
      return;
    }
    const tempId = crypto.randomUUID();
    setMessages((prev) => [...prev, {
      id: tempId, temp: true, status: "sending", chat_id: chatId,
      sender_id: user.id, sender_name: user.name, sender_avatar: user.avatar,
      type: "text", text: body, created_at: new Date().toISOString(),
      reply_to: reply?.id, reply: reply ? { id: reply.id, sender_name: reply.sender_name, text: reply.type === "image" ? ru.chats.photo : reply.text?.slice(0, 160) } : null,
    }]);
    atBottomRef.current = true;
    setText(""); setReply(null);
    doSend(body, tempId, reply?.id);
  };

  const retry = (m) => {
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, status: "sending" } : x)));
    doSend(m.text, m.id, m.reply_to);
  };

  const selectPhoto = (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.size || file.size > 5 * 1024 * 1024 || !["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error(ru.chats.photoLimit); return; }
    setPhoto(file); setUploadFailed(false); setEditing(null); inputRef.current?.focus();
  };
  const replyTo = (m) => { if (uploading) return; setReply(m); setEditing(null); inputRef.current?.focus(); };
  const editMessage = (m) => { if (uploading) return; setEditing(m); setReply(null); setPhoto(null); setText(m.text); inputRef.current?.focus(); };
  const deleteMessage = async (m) => {
    if (!window.confirm(ru.chats.deleteConfirm)) return;
    try { await api.delete(`/chats/${chatId}/messages/${m.id}`); }
    catch (err) { toast.error(err.response?.data?.detail || ru.errors.generic); }
  };
  const jumpTo = async (id) => {
    if (jumpingRef.current) return;
    setShowSearch(false); atBottomRef.current = false;
    const existing = document.getElementById(`message-${id}`);
    if (existing) { existing.scrollIntoView({ block: "center", behavior: "smooth" }); existing.animate([{ outline: "3px solid #34C759" }, { outline: "3px solid transparent" }], { duration: 1800 }); return; }
    jumpingRef.current = true;
    try {
      let before = messagesRef.current.find(m => !m.temp)?.id, collected = [], more = true;
      while (more) {
        const { data } = await api.get(`/chats/${chatId}/messages`, { params: { before, limit: 50 } });
        collected.push(...data.messages); more = data.has_more; before = data.messages.at(-1)?.id;
        if (collected.some(m => m.id === id) || !before || before < id) break;
      }
      setHasMore(more);
      if (collected.some(m => m.id === id)) quoteTargetRef.current = id;
      else toast.error(ru.chats.originalMissing);
      setMessages(prev => [...collected.reverse().filter(m => !prev.some(p => p.id === m.id)), ...prev]);
    } catch { toast.error(ru.errors.network); }
    finally { jumpingRef.current = false; }
  };
  const searchMessages = async (e) => {
    e.preventDefault(); if (!query.trim() || !chatId) return;
    try { const { data } = await api.get(`/chats/${chatId}/messages`, { params: { q: query.trim() } }); setResults(data.messages); }
    catch { toast.error(ru.errors.network); }
  };

  const onInput = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (chatId && now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      send("typing", { chat_id: chatId });
    }
  };

  const peerOnline = peer && (online[peer.id] ?? peer.online);
  const title = isGlobal ? ru.chats.global : peer?.name || "";

  let lastDay = null;

  return (
    <div className="flex flex-col h-[calc(100dvh-var(--push-banner-height,0px))]" data-testid="chat-view">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white/90 backdrop-blur-xl shrink-0">
        <button data-testid="chat-back-button" onClick={() => navigate("/chats")} className="p-2 rounded-full bg-gray-100 active:scale-[0.95] transition-transform">
          <ArrowLeft size={18} />
        </button>
        {isGlobal ? (
          <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center shrink-0">
            <Users size={18} className="text-white" />
          </div>
        ) : peer ? (
          <Link to={`/users/${peer.id}`} data-testid="chat-peer-profile">
            <Avatar avatar={peer.avatar} name={peer.name} size={40} />
          </Link>
        ) : null}
        <div className="min-w-0">
          <p className="font-bold truncate" data-testid="chat-title">{title}</p>
          <p className="text-xs text-gray-400" data-testid="chat-subtitle">
            {typingName
              ? `${isGlobal ? typingName + " " : ""}${ru.chats.typing}`
              : isGlobal
              ? ru.chats.members
              : peerOnline
              ? ru.chats.online
              : ru.chats.offline}
          </p>
        </div>
        {!isGlobal && peer && (
          <>
            <button
              data-testid="chat-call-button"
              onClick={() => startCall({ id: peer.id, name: peer.name, avatar: peer.avatar })}
              title={ru.calls.call}
              className="ml-auto p-2.5 rounded-full bg-gray-100 active:scale-[0.95] transition-transform shrink-0"
            >
              <Phone size={18} />
            </button>
            {peer.mutual && (
              <button
                data-testid="chat-video-call-button"
                onClick={() => startCall({ id: peer.id, name: peer.name, avatar: peer.avatar }, "video")}
                title={ru.calls.videoCall}
                className="p-2.5 rounded-full bg-black text-white active:scale-[0.95] transition-transform shrink-0"
              >
                <Video size={18} />
              </button>
            )}
          </>
        )}
        {chatId && <button data-testid="chat-search-toggle" aria-label={ru.chats.search} onClick={() => setShowSearch(!showSearch)} className="p-2 rounded-full bg-gray-100 ml-auto"><Search size={18} /></button>}
      </header>
      {showSearch && <div className="px-4 py-3 border-b max-h-[35vh] overflow-y-auto" data-testid="chat-search-panel"><form onSubmit={searchMessages} className="flex gap-2"><input data-testid="chat-search-input" value={query} onChange={e => setQuery(e.target.value)} placeholder={ru.chats.search} maxLength={200} className="min-w-0 flex-1 bg-gray-100 rounded-xl px-3 py-2 text-sm" /><button data-testid="chat-search-submit" disabled={!query.trim()} className="text-sm font-bold">{ru.chats.searchButton}</button></form>{results?.length === 0 && <p data-testid="chat-search-empty" className="text-sm text-gray-500 mt-3">{ru.chats.noResults}</p>}{results?.map(m => <button data-testid={`chat-search-result-${m.id}`} key={m.id} onClick={() => jumpTo(m.id)} className="block text-left w-full py-2 border-b text-sm"><strong className="block text-xs">{m.sender_name}</strong>{m.type === "image" && ru.chats.photo} {m.text}</button>)}</div>}

      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-3" data-testid="messages-container">
        {messages.length === 0 && (
          <p className="text-center text-gray-400 text-sm mt-10" data-testid="no-messages">{ru.chats.noMessages}</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          const day = dayLabel(m.created_at);
          const showDay = day !== lastDay;
          lastDay = day;
          if (m.type === "system" || (m.type === "order" && m.order_id)) {
            return (
              <div key={m.id}>
                {showDay && (
                  <div className="flex justify-center my-3">
                    <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{day}</span>
                  </div>
                )}
                {m.type === "system" ? (
                  <div className="flex justify-center my-2" data-testid={`system-message-${m.id}`}>
                    <span className="text-[12px] text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full max-w-[85%] text-center">
                      {m.text}
                    </span>
                  </div>
                ) : (
                  <div className="my-2.5" data-order-id={m.order_id} data-testid={`order-message-${m.id}`}>
                    {orders[m.order_id] ? (
                      <OrderCard order={orders[m.order_id]} />
                    ) : (
                      <div className="rounded-2xl border border-gray-200 p-4 text-sm text-gray-400" data-testid={`${missingOrders.has(m.order_id) ? "order-unavailable" : "order-placeholder"}-${m.id}`}>
                        {missingOrders.has(m.order_id) ? ru.orders.unavailable : failedOrders.has(m.order_id) ? ru.orders.loadFailed : ru.orders.loading}
                        {!missingOrders.has(m.order_id) && failedOrders.has(m.order_id) && <button data-testid={`order-retry-${m.id}`} onClick={() => ensureOrders([m.order_id], true)} className="block underline mt-2">{ru.chats.retry}</button>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div key={m.id}>
              {showDay && (
                <div className="flex justify-center my-3">
                  <span className="text-[11px] font-semibold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{day}</span>
                </div>
              )}
              <div className={`flex gap-2 mb-2 ${mine ? "justify-end" : "justify-start"}`}>
                {!mine && isGlobal && (
                  <Link to={`/users/${m.sender_id}`} data-testid={`message-profile-${m.id}`} className="shrink-0 self-end">
                    <Avatar avatar={m.sender_avatar} name={m.sender_name} size={28} />
                  </Link>
                )}
                <MessageBubble message={m} mine={mine} isGlobal={isGlobal} lastRead={lastRead} onReply={replyTo} onEdit={editMessage} onDelete={deleteMessage} onQuote={jumpTo} onPhoto={setViewer} onRetry={retry} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-100 bg-white shrink-0 pb-[max(env(safe-area-inset-bottom),12px)]">
        {(reply || editing) && <div data-testid="composer-quote" className="mx-4 mt-3 border-l-2 border-emerald-500 bg-gray-50 rounded-r-xl px-3 py-2 flex items-center gap-3"><div className="min-w-0 flex-1"><p className="text-xs font-bold text-emerald-600">{editing ? ru.chats.editing : reply.sender_name}</p><p className="text-xs truncate">{editing ? editing.text : reply.deleted ? ru.chats.deleted : reply.type === "image" ? ru.chats.photo : reply.text}</p></div><button disabled={uploading} data-testid="composer-quote-cancel" aria-label={ru.chats.cancel} onClick={() => { setReply(null); if (editing) setText(""); setEditing(null); }}><X size={18} /></button></div>}
        {photo && <div data-testid="photo-preview" className="mx-4 mt-3 relative flex gap-3 items-center bg-gray-50 rounded-xl p-3"><img src={photoUrl} alt={ru.chats.photo} className="w-20 h-20 rounded-lg object-cover" data-testid="photo-preview-image" /><div className="min-w-0 flex-1 text-xs"><p className="truncate">{photo.name}</p><p data-testid="photo-upload-status" className={uploadFailed ? "text-red-500 mt-2" : "text-gray-500 mt-2"}>{uploading ? progress === 100 ? ru.chats.processing : ru.chats.uploadProgress(progress) : uploadFailed ? ru.chats.photoFailed : ru.chats.photoLimit}</p>{uploading && <progress data-testid="photo-upload-progress" className="w-full mt-2 accent-black" max={100} value={progress} />}</div><button type="button" disabled={uploading} data-testid="photo-remove" aria-label={ru.chats.cancel} onClick={() => setPhoto(null)}><X size={18} /></button></div>}
        <form onSubmit={submit} className="flex items-center gap-2 px-4 pt-3">
          <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={selectPhoto} />
          <button data-testid="attach-photo" type="button" aria-label={ru.chats.attach} disabled={uploading || !!editing} onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30"><Paperclip size={20} /></button>
          <input ref={inputRef} data-testid="message-input" value={text} onChange={onInput} disabled={uploading} placeholder={photo ? ru.chats.caption : ru.chats.inputPlaceholder} maxLength={2000} className="flex-1 min-w-0 h-11 px-4 bg-gray-100 rounded-full text-[15px] outline-none focus:ring-2 focus:ring-black" />
          <button data-testid="send-button" aria-label={editing ? ru.chats.save : uploadFailed ? ru.chats.retry : ru.chats.send} type="submit" disabled={(!text.trim() && !photo) || uploading} className="w-11 h-11 bg-black text-white rounded-full flex items-center justify-center active:scale-[0.92] transition-transform disabled:opacity-30 shrink-0"><Send size={18} /></button>
        </form>
      </div>
      {viewer && <PhotoViewer message={viewer} onClose={closeViewer} />}
    </div>
  );
}
